import { useState } from 'react';
import toast from 'react-hot-toast';
import { ConfirmDialog } from '../Modal.tsx';
import { BroadcastIcon } from './BroadcastIcon.tsx';
import { usePermissions } from '../../auth/session.tsx';
import { useIsScopedMember } from '../../api/org.ts';
import { useCurrentBroadcast, useCancelBroadcast } from '../../api/broadcast.ts';
import { ApiError } from '../../api/client.ts';

function hhmm(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function truncate(s: string, n = 60): string {
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s;
}

/**
 * Persistent org-wide banner shown across the Studio whenever a broadcast is live
 * (fed by GET /broadcast/current, polled 30s + refetched on focus, so it appears
 * and clears within a heartbeat for every teammate). Full-access members get a
 * Cancel action (confirm → DELETE → banner clears via query invalidation);
 * viewers and scoped editors see the status without the control they can't use.
 */
export function BroadcastBanner() {
  const current = useCurrentBroadcast();
  const cancel = useCancelBroadcast();
  const { isViewer } = usePermissions();
  const scoped = useIsScopedMember();
  const [confirming, setConfirming] = useState(false);

  const b = current.data;
  if (!b) return null;

  const canCancel = !isViewer && scoped !== true;

  return (
    <div className="bcast-banner" role="status" aria-live="polite">
      <span className="bcast-banner-ico" aria-hidden>
        <BroadcastIcon />
      </span>
      <span className="bcast-banner-text">
        <strong>Broadcast live</strong> since {hhmm(b.started_at)}
        <span className="bcast-banner-sep" aria-hidden>
          ·
        </span>
        <span className="bcast-banner-msg">{truncate(b.message)}</span>
      </span>
      {canCancel && (
        <button
          type="button"
          className="btn sm bcast-banner-cancel"
          onClick={() => setConfirming(true)}
        >
          Cancel
        </button>
      )}

      {confirming && (
        <ConfirmDialog
          title="Cancel broadcast?"
          message="All screens return to their normal schedule immediately."
          confirmLabel="Cancel broadcast"
          danger
          busy={cancel.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() =>
            cancel.mutate(b.schedule_id, {
              onSuccess: () => {
                toast.success('Broadcast cancelled');
                setConfirming(false);
              },
              onError: (e) => {
                setConfirming(false);
                toast.error(e instanceof ApiError ? e.message : 'Could not cancel');
              },
            })
          }
        />
      )}
    </div>
  );
}
